import "#app/global.css";

import { ThemeProvider } from "@salestrak/ui";
import {
  createRootRoute,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import React from "react";

import { AuthProvider, useAuth } from "#app/lib/auth-context";
import { SplashScreen } from "#app/components/brand/splash-screen";
import { AppProviders } from "#app/lib/app-providers";
import { AppErrorBoundary } from "#app/lib/route-error-boundary";
import { useVersionCheck } from "#app/lib/use-version-check";

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    /**
     * Set on an (auth) screen that works for an ALREADY-SIGNED-IN user, which
     * suppresses the bounce into the app below. The emailed set-password link
     * is the case that needs it: it is requested from inside the app, so the
     * session that asked for it is exactly the one that opens it. The flag
     * lives on the route rather than in a path list here for the same reason
     * `inAuthGroup` reads route ids — a list in this file silently bounced
     * every screen it didn't know about.
     */
    allowAuthenticated?: boolean;
  }
}

/**
 * Inner navigator with access to auth context.
 * Redirects between (auth) and (app) route groups based on auth state.
 */
function RootNavigator() {
  const { state } = useAuth();
  // Membership in the (auth) group comes from the matched route ids, not a
  // path list — a hardcoded list here silently bounced every auth screen it
  // didn't know about back to /login the moment one was added.
  const inAuthGroup = useRouterState({
    select: (s) => s.matches.some((m) => m.routeId.startsWith("/(auth)")),
  });
  // Deliberately a SECOND flag rather than an exclusion from `inAuthGroup`:
  // these screens still belong to the group, so an unauthenticated visitor
  // must be left on them instead of being sent to /login.
  const allowsAuthenticated = useRouterState({
    select: (s) => s.matches.some((m) => m.staticData.allowAuthenticated === true),
  });
  const navigate = useNavigate();

  useVersionCheck();

  React.useEffect(() => {
    if (state.isAuthenticated === null) return;

    if (state.isAuthenticated && inAuthGroup && !allowsAuthenticated) {
      navigate({ to: "/" });
    } else if (!state.isAuthenticated && !inAuthGroup) {
      navigate({ to: "/login" });
    }
  }, [state.isAuthenticated, inAuthGroup, allowsAuthenticated, navigate]);

  return <Outlet />;
}

const SPLASH_MIN_MS = 1000;

function RootComponent() {
  const [splashDone, setSplashDone] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setSplashDone(true), SPLASH_MIN_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    // #root is a full-height flex column via global.css, so no wrapper
    // element is needed here. `storageKey="theme_mode"` keeps every existing
    // user's stored light|dark|system value working.
    <AuthProvider>
      <ThemeProvider defaultTheme="dark" storageKey="theme_mode">
        <AppProviders>
          <AppErrorBoundary>
            <RootNavigator />
          </AppErrorBoundary>
        </AppProviders>
      </ThemeProvider>

      {!splashDone && <SplashScreen />}
    </AuthProvider>
  );
}

export const Route = createRootRoute({ component: RootComponent });
